<template>
    <div>
    <div class="pt-3 fill-height myprovet-shopping-cart-list" v-if="!isMobile">
        <v-row class="no-gutters px-4 mb-0">
            <v-col cols="12" :style="calculateHeaderColWidth('designation')">
                <div class="d-flex fill-height align-center justify-start">
                    <strong class="font-size-md dark-blue-color">{{ $t('campaigns.product') }}</strong>
                </div>
            </v-col>

            <v-col cols="12" :style="calculateHeaderColWidth('quantity')">
                <div class="d-flex fill-height align-center justify-center">
                    <strong class="font-size-md dark-blue-color">{{ $t('orders.base.quantity') }}</strong>
                </div>
            </v-col>

            <v-col cols="12" :style="calculateHeaderColWidth('bonus')">
                <div class="d-flex fill-height align-center justify-center">
                    <strong class="font-size-md dark-blue-color">{{ $t('orders.base.bonus') }}</strong>
                </div>
            </v-col>

            <v-col v-if="userStore.hasPermissionTo('View prices')" :style="calculateHeaderColWidth('price')">
                <div class="d-flex fill-height align-center justify-center">
                    <strong class="font-size-md dark-blue-color">{{ $t('campaigns.unitPrice') }}</strong>
                </div>
            </v-col>

            <v-col cols="12" :style="calculateHeaderColWidth('tax')">
                <div class="d-flex fill-height align-center justify-center">
                    <strong class="font-size-md dark-blue-color">{{ $t('orders.base.vat') }}</strong>
                </div>
            </v-col>

            <v-col v-if="userStore.hasPermissionTo('View prices')" :style="calculateHeaderColWidth('total')">
                <div class="d-flex fill-height align-center justify-center">
                    <strong class="font-size-md dark-blue-color">{{ $t('campaigns.value') }}</strong>
                </div>
            </v-col>
        </v-row>

        <v-list flat class="px-2 pt-0 myprovet-shopping-cart-list">
            <v-list-item-group :ripple="false">
                <v-list-item data-cy="shopping-cart-item" :ripple="false" :key="product.uniqueId" :class="getLineColor(product)" style="cursor: default">
                    <div class="myprovet-list-item-row mr-0" style="height: 58px">
                        <div :style="calculateColWidth('designation')" class="myprovet-list-item-content pl-4">
                            <div class="d-flex fill-height align-center justify-start">
                                <product-image :productCode="product.invoice_code" :imageSrc="product.image" :is-new="!!product.new" width="35" height="35" max-width="35" max-height="35" class="mr-2" style="cursor: pointer; position: relative" @click="openProductDetails(product)"></product-image>

                                <v-list-item-content @click="openProductDetails(product)" class="py-0 myprovet-list-item-content-clickable">
                                    <p class="shopping-cart-product-title" data-cy="cart-item-designation" v-text="product.designation"></p>
                                    <v-list-item-subtitle v-if="product.code" style="font-size: 10px; margin-bottom: -4px; line-height: 1.5">{{ product.code }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </div>
                        </div>

                        <div class="myprovet-list-item-content" :style="calculateColWidth('quantity')">
                            <div class="d-flex fill-height align-center justify-center">
                                <quantity-counter
                                    v-if="!product.campaign_id"
                                    :value="storeQuantity > 0 ? storeQuantity : product.quantity"
                                    :min="product.bonus > 0 ? 0 : 1"
                                    :max="normalOrderStore.maxQuantity"
                                    @input="onQuantityUpdated(product, $event)"
                                    @decrement="increment(product, -1)"
                                    @increment="increment(product, 1)"
                                    input-class="shopping-cart-input minimum-input-size"
                                    :hide-counter="branchStore.cannotPurchase"
                                    :hide-input="branchStore.cannotPurchase"
                                ></quantity-counter>
                            </div>
                        </div>

                        <div class="myprovet-list-item-content" :style="calculateHeaderColWidth('bonus')">
                            <div class="d-flex fill-height align-center justify-center">
                                <quantity-counter
                                    :readonly="!canChangeBonusQuantity"
                                    :hide-counter="!canChangeBonusQuantity || branchStore.cannotPurchase"
                                    :hide-input="!canChangeBonusQuantity || branchStore.cannotPurchase"
                                    :value="storeQuantityBonus > 0 ? storeQuantityBonus : product.bonus"
                                    :min="product.quantity > 0 ? 0 : 1"
                                    input-class="shopping-cart-input minimum-input-size"
                                    @input="tryChangeProductBonus(product, $event, true)"
                                    @increment="tryChangeProductBonus(product, 1)"
                                    @decrement="tryChangeProductBonus(product, -1)"
                                ></quantity-counter>
                            </div>
                        </div>

                        <div v-if="userStore.hasPermissionTo('View prices')" class="myprovet-list-item-content" :style="calculateColWidth('price')">
                            <div class="d-flex fill-height align-center justify-end cart-dialog-price" style="width: fit-content">
                                <template v-if="product.discount && !noCampaign">
                                    <v-list-item-content class="py-0 shopping-cart-product-title text-center">
                                        <p class="red--text text-decoration-strikethrough" style="font-size: 0.75rem !important">{{ formatPrice(product.price) }}</p>
                                        <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem !important; margin-bottom: -4px; line-height: 1.5">{{ formatPrice(product.price - product.price * (product.discount / 100)) }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>

                                <template v-else-if="product.value && !noCampaign">
                                    <v-list-item-content class="py-0 shopping-cart-product-title text-center">
                                        <p class="red--text text-decoration-strikethrough" style="font-size: 0.75rem !important">{{ formatPrice(product.price) }}</p>
                                        <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem !important; margin-bottom: -4px; line-height: 1.5">{{ formatPrice(product.value) }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>

                                <template v-else>
                                    <p class="shopping-cart-product-title mt-5" data-cy="cart-item-price">{{ formatPrice(product.price) }}</p>
                                </template>

                                <product-discounts-icon v-if="userStore.hasPermissionTo('View discounts icon')" :product="product"></product-discounts-icon>
                            </div>
                        </div>

                        <div class="myprovet-list-item-content" :style="calculateColWidth('tax')" v-if="showTax(product)">
                            <div class="d-flex fill-height align-center justify-end">
                                <p class="shopping-cart-product-title mt-5" data-cy="cart-item-tax">{{ product.vat.tax }} %</p>
                            </div>
                        </div>

                        <div v-if="userStore.hasPermissionTo('View prices')" class="myprovet-list-item-content" :style="calculateColWidth('total')">
                            <div class="d-flex fill-height align-center justify-end">
                                <p class="shopping-cart-product-title mt-5" data-cy="cart-item-sub-total-price">{{ formatPrice(getProductSubTotalPrice(product)) }}</p>
                            </div>
                        </div>

                        <div class="pr-0 myprovet-list-item-content" :style="calculateColWidth('actions')">
                            <div class="d-flex fill-height align-center justify-center">
                                <div class="mr-1 myprovet-cart-item-button">
                                    <product-comment v-if="product.quantity > 0 || product.bonus > 0" :product="product"></product-comment>
                                </div>

                                <v-tooltip v-if="product.quantity > 0 || product.bonus > 0" bottom open-on-hover content-class="myprovet-tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color" @click="confirmItemRemoval(product)" v-on="on" v-bind="attrs" data-cy="remove-product"> mdi-delete-outline </v-icon>
                                    </template>

                                    <span>{{ $t('campaigns.removeProduct') }}</span>
                                </v-tooltip>
                            </div>
                        </div>
                    </div>
                </v-list-item>
            </v-list-item-group>
        </v-list>

        <confirmation-dialog :value="isConfirmDialogOpen" @onSuccess="remove(selectedProduct)" @onCancel="isConfirmDialogOpen = false" :title="$t('layout.attention')" :question="$t('campaigns.removeProductQuestion')"> </confirmation-dialog>
    </div>
    <div class="pt-3 fill-height myprovet-shopping-cart-list product-shopping-cart" v-else>
    <v-row>
        <v-col class="d-flex flex-column col pt-0 pb-0">
            <v-row class="d-flex align-start pt-0 pb-0">
                <v-col class="col col-3 pt-0 pb-0">
                    <product-image :productCode="product.invoice_code" :imageSrc="product.image" :is-new="!!product.new" width="84" height="84" max-width="84" max-height="84" style="cursor: pointer; position: relative" @click="openProductDetails(product)"></product-image>

                </v-col>
                <v-col class="col col-9 pt-0 pb-0">
                    <v-row class="d-flex align-start pt-0 pb-0">
                        <v-col class="col col-12 pt-0 pb-0">
                            <div class="pl-0 datatable-designation-text" style="cursor: pointer" @click="openProductDetails(product)">
                                {{ product.designation }}
                            </div>
                        </v-col>
                        <v-col class="col col-12 pt-0 pb-0">
                            <div class="pl-0 datatable-lable-text laboratory">
                                {{ product.code}}
                            </div>
                        </v-col>
                        <v-col class="col col-12 pt-0 pb-0 stock">
                            <div class="price">
                                <template v-if="product.discount">
                                    <v-list-item-content class="py-0">
                                        <div class="flex-column" style="line-height: 18px">
                                            <p class="red--text text-decoration-strikethrough mb-0" style="font-size: 0.75rem">{{ formatPrice(product.price) }}</p>
                                            <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem">{{ formatPrice(product.price - product.price * (product.discount / 100)) }}</v-list-item-subtitle>
                                        </div>
                                    </v-list-item-content>
                                </template>

                                <template v-else-if="product.value">
                                    <v-list-item-content class="py-0">
                                        <div class="flex-column" style="line-height: 18px">
                                            <p class="red--text text-decoration-strikethrough mb-0" style="font-size: 0.75em">{{ formatPrice(product.price) }}</p>
                                            <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem">{{ formatPrice(product.value) }}</v-list-item-subtitle>
                                        </div>
                                    </v-list-item-content>
                                </template>

                                <template v-else>
                                    {{ formatPrice(product.price) }}
                                </template>
                            </div>
                            <div class="btn-actions">
                                <div class="mr-1 myprovet-cart-item-button">
                                    <v-tooltip v-if="product.quantity > 0" bottom open-on-hover content-class="myprovet-tooltip">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color" @click="confirmItemRemoval(product)" v-on="on" v-bind="attrs" data-cy="remove-product"> mdi-delete-outline </v-icon>
                                        </template>

                                        <span>{{ $t('campaigns.removeProduct') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="mr-1 myprovet-cart-item-button">
                                    <product-comment v-if="product.quantity > 0" :product="product"></product-comment>
                                </div>

                            </div>
                            <!--<v-tooltip bottom open-on-hover content-class="myprovet-tooltip" data-cy="stocks">
                                                                <template v-slot:activator="{ on, attrs }">
                                                                    <v-icon style="" :color="isAvailable(product) ? 'green' : 'red'" v-on="on" v-bind="attrs">mdi-circle</v-icon>
                                                                </template>

                                                                <span>{{ isAvailable(product) ? $t('orders.base.stockAvailable') : $t('orders.base.confirmStock') }}</span>
                                                            </v-tooltip>
                                                            <product-additional-info :product="product"> </product-additional-info>-->
                        </v-col>
                    </v-row>
                </v-col>
                <v-col class="col col-12 pt-0 pb-0">
                </v-col>
                <v-col class="col col-12 pt-0 pb-0">
                    <v-row class="d-flex align-start pt-0 pb-0">
                        <v-col class="col col-6 pt-0 pb-0 pl-0">
                            <div class="col pt-0 pb-0 label">{{ $t('orders.base.quantity') }}</div>
                            <quantity-counter v-if="!product.campaign_id"
                                :hide-counter="branchStore.cannotPurchase"
                                :hide-input="branchStore.cannotPurchase"
                                :value="product.quantity"
                                :min="product.bonus > 0 ? 0 : 1"
                                :max="normalOrderStore.maxQuantity"
                                @input="onQuantityUpdated(product, $event)"
                                @decrement="increment(product, -1)"
                                @increment="increment(product, 1)"
                                input-class="shopping-cart-input minimum-input-size">
                            </quantity-counter>
                        </v-col>
                        <v-col class="col col-6 pt-0 pb-0 pl-0">
                            <div class="col pt-0 pb-2 empty"></div>
                            <div class="col pt-0 pb-2  pl-0 pr-0 label lf ">
                                <div class="l-sub">{{ $t('orders.base.vat') }}</div>
                                <div class="l-sub-value">{{product.vat.tax}} %</div>
                            </div>

                        </v-col>
                    </v-row>
                </v-col>
                <v-col class="col col-12 pt-0 pb-0">
                    <v-row class="d-flex align-start pt-0 pb-0">
                        <v-col class="col col-6 pt-0 pb-0 pl-0">
                            <div class="col pt-0 pb-0 label">{{ $t('orders.base.bonus') }}</div>
                            <quantity-counter
                                :readonly="!canChangeBonusQuantity"
                                :hide-counter="!canChangeBonusQuantity || branchStore.cannotPurchase"
                                :hide-input="!canChangeBonusQuantity || branchStore.cannotPurchase"
                                :value="product.bonus"
                                :min="product.quantity > 0 ? 0 : 1"
                                input-class="shopping-cart-input minimum-input-size"
                                @input="onBonusQuantityUpdated(product, $event)"
                                @increment="incrementBonus(product, 1)"
                                @decrement="incrementBonus(product, -1)">
                            </quantity-counter>
                        </v-col>
                        <v-col class="col col-6 pt-0 pb-0 pl-0">
                            <div class="col pt-0 pb-2 empty"></div>
                            <div class="col pt-0 pb-2  pl-0 pr-0 label lf ">
                                <div class="l-sub">{{ $t('campaigns.value') }}</div>
                                <div class="l-sub-value" v-if="userStore.hasPermissionTo('View prices')">

                                    <p class="shopping-cart-product-title " data-cy="cart-item-sub-total-price">{{ formatPrice(getProductSubTotalPrice(product)) }}</p>

                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-col>

            </v-row>
        </v-col>
    </v-row>
    <confirmation-dialog :value="isConfirmDialogOpen" @onSuccess="remove(selectedProduct)" @onCancel="isConfirmDialogOpen = false" :title="$t('layout.attention')" :question="$t('campaigns.removeProductQuestion')"> </confirmation-dialog>
</div>
<v-dialog
            v-model="isOfferTypeDialogOpen"
            persistent
            max-width="380"
            >
                <v-card class="myprovet-dialog-card select-offer-type" :max-height="200" :height="160" data-cy="app-dialog-card">
                <!--<v-card-title v-if="!isAlertDialog" class="d-flex justify-space-between align-center title-text dark-blue-color mx-1 px-6" data-cy="app-dialog-title">
                    Selecionar a origem do bónus
                    <v-spacer></v-spacer>
                </v-card-title>-->
                <v-card-text class="px-6 pb-2 pt-6">
                    <v-select
                    style="width:300px; font-size: 1rem!important;"
                    :items="bonusTypes"
                    v-model="currentOfferType"
                    :label="$t('orders.base.selectBonusType')"
                    @change="setBonusTypes"
                    ></v-select>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import { mapStores } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useUserStore } from '@/Stores/user';
import { useNormalOrderStore } from '@/Stores/normalorder';
import { useAppStore } from '@/Stores/app';

export default {
    props: {
        product: Object,

        noCampaign: {
            type: Boolean,
            default: false,
        },

        currentSearchTerm: String,
        accountBlockedMessage: String,
    },

    data: () => ({
        comment: null,
        selectedProduct: {},

        isConfirmDialogOpen: false,
        isOfferTypeDialogOpen: false,
        bonusTypes:['BONUS FORN','BONUS PROP'],
        currentOfferType:null,
        currentBonus:null,
        isAbsoluteAmountValue:false,
        amount:0,
    }),

    watch: {
        storeQuantity: {
            handler: function (value) {
                this.quantity = value ?? 0;
            },
            immediate: true,
        },
        storeQuantityBonus: {
            handler: function (value) {
                this.bonus = value ?? 0;
            },
            immediate: true,
        },
    },

    computed: {
        ...mapStores(useBranchStore, useNormalOrderStore, useUserStore,useAppStore),

        canChangeBonusQuantity() {
            return   this.userStore.hasPermissionTo('Change bonus quantity (as propec)') || this.userStore.hasPermissionTo('Change bonus quantity (as supplier)');
        },

        storeQuantity() {
            const item = this.normalOrderStore.getProduct(this.product.id);
            return item?.quantity ?? 0;
        },
        storeQuantityBonus() {
            const item = this.normalOrderStore.getProduct(this.product.id);
            this.product.bonus= item?.bonus?? 0
            this.product.offer_type=item?.offer_type??null
            return item?.bonus?? 0;
        },
        isMobile(){
            return this.appStore.mobile_ls?false:this.appStore.is_mobile;
        },
    },

    methods: {
        showTax(prod){
         if(prod.tax){
            return true
         }else{
            return false
         }
        },
        calculateHeaderColWidth(field) {
            switch (field) {
            case 'designation':
                if (!this.userStore.hasPermissionTo('View prices')) {
                    return this.calculateColWidth(field);
                }

                return 'width: 38%; max-width: 38%';
            default:
                return this.calculateColWidth(field);
            }
        },

        calculateColWidth(field) {
            switch (field) {
            case 'designation':
                if (!this.userStore.hasPermissionTo('View prices')) {
                    return 'width: 52%; max-width: 52%';
                }

                return 'width: 38%; max-width: 38%';

            case 'quantity':
            case 'bonus':
                return 'width: 15%; max-width: 15%';

            case 'price':
            case 'tax':
            case 'total':
                return 'width: 8%; max-width: 8%';

            case 'actions':
                return 'width: 8%; max-width: 8%';

            default:
                return '';
            }
        },

        openProductDetails(product) {
            this.$emit('open-product-details', product);
        },

        confirmItemRemoval(product) {
            if(!this.checkConnection()){
                this.showError(this.$t('common.noInternet'));
                return
            }
            this.isConfirmDialogOpen = true;
            this.selectedProduct = product;
        },

        remove(product) {
            if(!this.checkConnection()){
                this.showError(this.$t('common.noInternet'));
                return
            }
            this.normalOrderStore.removeProduct(product.id);
            this.isConfirmDialogOpen = false;
        },

        hideQuantityCounter(product) {
            if (product.isHeader) return false;
            if (product.isOffer) return true;
        },

        getProductSubTotalPrice(product) {
            const withDiscount = this.normalOrderStore.calculatePriceWithDiscount(product);
            const withoutDiscount = product.price;

            return this.normalOrderStore.calculateWithExceedingQuantity(product, withDiscount, withoutDiscount);
        },

        setSelectedProduct() {
            this.$emit('selected-product', this.selectedProduct);
        },

        onQuantityUpdated(product, amount) {
            if (this.branchStore.isBlocked) {
                this.showError(this.accountBlockedMessage);
                return;
            }

            const quantity = this.parseQuantity(amount);

            if (!this.normalOrderStore.isInCart(product) && quantity !== 0) {
                product.quantity = quantity;

                this.normalOrderStore.addProduct(product, this.currentSearchTerm);
                return;
            }

            if (this.normalOrderStore.isInCart(product)) {
                if (quantity === 0) {
                    this.normalOrderStore.removeProduct(product.id);
                    return;
                }

                this.normalOrderStore.setProductQuantity(product.id, quantity);
            }
        },

        increment(product, amount) {
            if(!this.checkConnection()){
                this.showError(this.$t('common.noInternet'));
                return
            }

            if (this.branchStore.isBlocked) {
                this.showError(this.accountBlockedMessage);
                return;
            }

            if (product.quantity === this.normalOrderStore.getMaxQuantity && amount === 1) {
                return;
            }

            if (!this.normalOrderStore.isInCart(product)) {
                if (amount < 0) return;

                product.quantity = 1;
                this.normalOrderStore.addProduct(product, this.currentSearchTerm);
                return;
            }

            // @Note: `amount` can be negative.
            if (product.bonus === 0 && product.quantity + amount <= 0) {
                this.normalOrderStore.removeProduct(product.id);
                return;
            }

            this.normalOrderStore.incrementProductQuantity(product.id, amount);
        },

        tryChangeProductBonus(product, amount, isAbsoluteAmountValue = false) {
            if(!this.checkConnection()){
                this.showError(this.$t('common.noInternet'));
                return
            }
            if (this.branchStore.isBlocked) {
                this.showError(this.accountBlockedMessage);
                return;
            }

            if(this.userStore.hasPermissionTo('Change bonus quantity (as propec)')){

                if(product.bonus == 0 && amount>0){
                    /*product['offer_type']=null
                    this.currentBonus = product
                    this.amount=amount
                    this.isAbsoluteAmountValue= isAbsoluteAmountValue
                    this.isOfferTypeDialogOpen=true
                    return;*/
                    product['offer_type']='BONUS FORN';
                }
                if(product.bonus == 1 && amount<=0){
                    product['offer_type']=null
                }
            }

            if (isAbsoluteAmountValue) product.bonus = this.parseQuantity(amount);

            if (product.bonus == 0 && amount === -1) return;

            this.changeProductBonus(product, amount, isAbsoluteAmountValue);
        },
        setBonusTypes(value){
            this.isOfferTypeDialogOpen=false
            this.currentBonus.offer_type=value
            this.$nextTick(_=>{
                this.currentOfferType=null
            })

            if (this.isAbsoluteAmountValue) this.currentBonus.bonus = this.parseQuantity(this.amount);
            if (this.currentBonus.bonus == 0 && this.amount === -1) return;

            /*if (!this.normalOrderStore.isInCart(this.currentBonus)) {
                if (this.normalOrderStore.isSpecialProduct(this.currentBonus)) {
                    this.onAcceptedCallback = () => this.checkIfBonusIsInPendingList(this.currentBonus, this.amount, this.isAbsoluteAmountValue);
                    this.selectedProduct = this.currentBonus;

                    this.isProductShippingConditionsDialogOpen = true;
                    return;
                }

                // @Note: It's not a special product, so check if the product is in the pending list.
                this.checkIfBonusIsInPendingList(this.currentBonus, this.amount, this.isAbsoluteAmountValue);
                return;
            }*/

            this.changeProductBonus(this.currentBonus, this.amount, this.isAbsoluteAmountValue);
        },
        getLineColor(item){

            if(this.userStore.hasPermissionTo('Change bonus quantity (as propec)')){

                if(_.has(item,'offer_type')){
                    if(item.offer_type==='BONUS PROP')
                        return ' bonus-prop px-0';
                    if(item.offer_type==='BONUS FORN')
                        return 'bonus-forn px-0';
                }
            }
            return ' px-0';
        },
        changeProductBonus(product, amount, isAbsoluteAmountValue) {
            if (!this.normalOrderStore.isInCart(product)) {
                if (amount < 0) return;

                product.bonus = amount;
                this.normalOrderStore.addProduct(product, this.currentSearchTerm);
                return;
            }

            // @Note: `amount` can be negative.
            if ((!isAbsoluteAmountValue && product.quantity === 0 && product.bonus + amount <= 0) || (isAbsoluteAmountValue && product.quantity === 0 && product.bonus <= 0)) {
                this.normalOrderStore.removeProduct(product.id);
                return;
            }

            if (isAbsoluteAmountValue) {
                this.normalOrderStore.setProductBonus(product.id, amount);
            } else {
                this.normalOrderStore.incrementProductBonus(product.id, amount);
            }
        },
    },
};
</script>
